import { useEffect, useState } from "react";
import client from "@/lib/api";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import AccessDenied from "@/components/AccessDenied";
import { KpiCard } from "@/components/KpiCard";
import { fmtNum } from "@/lib/format";
import { toast } from "sonner";
import { Clock, CheckCircle, Warning, DownloadSimple } from "@phosphor-icons/react";

const STATUS = {
    approved: { label: "Approved", bg: "bg-fp-success/10", color: "text-fp-success" },
    submitted: { label: "Submitted", bg: "bg-fp-navy/10", color: "text-fp-navy" },
    review: { label: "In Review", bg: "bg-fp-ochre/20", color: "text-fp-ink" },
    extension_granted: { label: "Extension", bg: "bg-fp-panel", color: "text-fp-secondary" },
    overdue: { label: "OVERDUE", bg: "bg-fp-terracotta/15", color: "text-fp-terracotta" },
    waived: { label: "Waived (dormant)", bg: "bg-fp-border", color: "text-fp-muted" },
};

export default function Submissions() {
    const [data, setData] = useState(null);
    const [denied, setDenied] = useState(false);
    const [filter, setFilter] = useState("all");
    const [templateFilter, setTemplateFilter] = useState("all");

    const load = () => client.get("/submissions")
        .then((r) => setData(r.data))
        .catch((e) => { if (e.response?.status === 403) setDenied(true); });
    useEffect(() => {
        load();
        const id = setInterval(load, 7000);
        return () => clearInterval(id);
    }, []);

    if (denied) return <AccessDenied />;
    if (!data) return <div className="p-16 font-serif text-3xl text-fp-ink">Loading…</div>;

    const rows = data.submissions.filter((s) =>
        (filter === "all" || s.status === filter) &&
        (templateFilter === "all" || s.template === templateFilter)
    );
    const s = data.stats;
    const complete = s.approved + s.submitted + s.review + s.extension_granted + s.waived;
    const pct = Math.round((complete / s.total) * 100);

    const grantExt = async (submissionId) => {
        const reason = prompt("Reason for extension?");
        if (!reason) return;
        try {
            await client.post("/submissions/action", { submission_id: submissionId, action: "extend", reason });
            toast.success("Extension granted");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const approve = async (submissionId) => {
        try {
            await client.post("/submissions/action", { submission_id: submissionId, action: "approve" });
            toast.success("Approved");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const download = (t) => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/templates/${t}`;
        const token = localStorage.getItem("fp_token");
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob()).then((blob) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `${t}.csv`; link.click();
            });
    };

    return (
        <div>
            <PageHeader
                overline={`Cycle ${data.cycle} · Cutoff 5th business day`}
                title="Submission Tracker"
                description="Monitor Trial Balance and Statistical KPI submissions across the group. Extensions granted only by Corporate Finance."
            >
                <div className="flex gap-2">
                    <button data-testid="dl-tb" onClick={() => download("trial_balance")} className="inline-flex items-center gap-2 border border-fp-ink text-fp-ink px-4 py-2 text-xs hover:bg-fp-panel transition-colors"><DownloadSimple size={14} /> TB Template</button>
                    <button data-testid="dl-stat" onClick={() => download("statistical_kpis")} className="inline-flex items-center gap-2 border border-fp-ink text-fp-ink px-4 py-2 text-xs hover:bg-fp-panel transition-colors"><DownloadSimple size={14} /> KPI Template</button>
                </div>
            </PageHeader>

            <div className="px-4 sm:px-6 lg:px-12 py-10 space-y-8">
                <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
                    <KpiCard label="Total Expected" value={fmtNum(s.total)} testId="stat-total" />
                    <KpiCard label="Approved" value={fmtNum(s.approved)} subValue={`${Math.round(s.approved / s.total * 100)}%`} />
                    <KpiCard label="Submitted" value={fmtNum(s.submitted)} />
                    <KpiCard label="In Review" value={fmtNum(s.review)} />
                    <KpiCard label="Extensions" value={fmtNum(s.extension_granted)} />
                    <KpiCard label="Overdue" value={fmtNum(s.overdue)} testId="stat-overdue" />
                </div>

                <div className="bg-white border border-fp-border p-6">
                    <div className="flex items-baseline justify-between mb-3">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted">Overall Progress</div>
                        <div className="font-mono text-3xl text-fp-ink">{pct}%</div>
                    </div>
                    <div className="w-full h-1.5 bg-fp-panel"><div className="h-full bg-fp-ink" style={{ width: `${pct}%` }} /></div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-sans text-xs text-fp-secondary">Filter:</span>
                    {["all", "overdue", "review", "submitted", "approved", "extension_granted"].map((f) => (
                        <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)} className={`px-3 py-1 text-xs font-sans transition-colors ${filter === f ? "bg-fp-ink text-white" : "border border-fp-border text-fp-secondary hover:border-fp-ink"}`}>{f.replace("_", " ")}</button>
                    ))}
                    <span className="ml-4 font-sans text-xs text-fp-secondary">Template:</span>
                    {["all", "trial_balance", "statistical_kpis"].map((t) => (
                        <button key={t} onClick={() => setTemplateFilter(t)} className={`px-3 py-1 text-xs font-sans transition-colors ${templateFilter === t ? "bg-fp-ink text-white" : "border border-fp-border text-fp-secondary hover:border-fp-ink"}`}>{t.replace("_", " ")}</button>
                    ))}
                </div>

                <div className="bg-white border border-fp-border p-6 overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b-2 border-fp-ink">
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Entity</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Template</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Country</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Preparer</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Submitted</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Status</th>
                                <th className="pb-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.slice(0, 100).map((sub) => {
                                const st = STATUS[sub.status] || STATUS.submitted;
                                return (
                                    <tr key={sub.id} className="border-b border-fp-border hover:bg-fp-bg" data-testid={`sub-${sub.id}`}>
                                        <td className="py-2.5 font-sans text-sm text-fp-ink"><Link to={`/entity/${sub.entity_id}/portal`} className="hover:underline">{sub.entity_name}</Link></td>
                                        <td className="py-2.5 font-sans text-xs text-fp-secondary">{sub.template_label}</td>
                                        <td className="py-2.5 font-mono text-xs text-fp-secondary">{sub.country_code}</td>
                                        <td className="py-2.5 font-sans text-xs text-fp-secondary">{sub.preparer.slice(0, 32)}</td>
                                        <td className="py-2.5 font-mono text-xs text-fp-secondary">{sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : "—"}</td>
                                        <td className="py-2.5"><span className={`px-2 py-0.5 text-xs font-sans ${st.bg} ${st.color}`}>{st.label}</span>{sub.days_late > 0 && <span className="ml-2 font-mono text-xs text-fp-terracotta">+{sub.days_late}d</span>}</td>
                                        <td className="py-2.5 text-right space-x-2">
                                            {sub.status === "submitted" && <button onClick={() => approve(sub.id)} className="text-xs text-fp-success hover:underline">Approve</button>}
                                            {sub.status === "overdue" && data.can_grant_extension && <button data-testid={`ext-${sub.id}`} onClick={() => grantExt(sub.id)} className="text-xs text-fp-terracotta hover:underline">Grant Extension</button>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {rows.length > 100 && <div className="mt-4 font-mono text-xs text-fp-muted">Showing 100 of {rows.length} — refine filters</div>}
                </div>
            </div>
        </div>
    );
}
